/**
 * Task Status Constants
 *
 * Centralized status definitions for consistent status handling across the app.
 * All statuses are lowercase to ensure case-insensitive matching.
 */

export const TASK_STATUS = {
  TODO: 'todo',
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  BLOCKED: 'blocked',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;

export type TaskStatus = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];

// Status groups for filtering and display
export const COMPLETED_STATUSES: TaskStatus[] = [TASK_STATUS.COMPLETED, TASK_STATUS.CANCELLED];
export const ACTIVE_STATUSES: TaskStatus[] = [TASK_STATUS.TODO, TASK_STATUS.OPEN, TASK_STATUS.IN_PROGRESS, TASK_STATUS.BLOCKED];

// Default status for new tasks
export const DEFAULT_TASK_STATUS: TaskStatus = TASK_STATUS.OPEN;

/**
 * Normalize any status string to a standard TaskStatus value.
 * Handles legacy values and case variations.
 */
export function normalizeStatus(status: string | null | undefined): TaskStatus {
  if (!status) return DEFAULT_TASK_STATUS;

  const normalized = status.toLowerCase().trim();

  // Map legacy/variant statuses to standard values
  switch (normalized) {
    case 'open':
    case 'todo':
      return TASK_STATUS.OPEN;
    case 'in progress':
    case 'in_progress':
    case 'in-progress':
    case 'inprogress':
      return TASK_STATUS.IN_PROGRESS;
    case 'blocked':
      return TASK_STATUS.BLOCKED;
    case 'completed':
    case 'closed':
    case 'done':
      return TASK_STATUS.COMPLETED;
    case 'cancelled':
    case 'canceled':
      return TASK_STATUS.CANCELLED;
    default:
      // Return as-is — custom statuses pass through unchanged
      return normalized as TaskStatus;
  }
}

/**
 * Check if a status represents a completed state
 */
export function isCompletedStatus(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status);
  return COMPLETED_STATUSES.includes(normalized);
}

/**
 * Check if a status represents an active (non-completed) state
 */
export function isActiveStatus(status: string | null | undefined): boolean {
  return !isCompletedStatus(status);
}

/**
 * Get display label for a status
 */
export function getStatusLabel(status: TaskStatus): string {
  switch (status) {
    case TASK_STATUS.TODO:
      return 'To Do';
    case TASK_STATUS.OPEN:
      return 'Open';
    case TASK_STATUS.IN_PROGRESS:
      return 'In Progress';
    case TASK_STATUS.BLOCKED:
      return 'Blocked';
    case TASK_STATUS.COMPLETED:
      return 'Completed';
    case TASK_STATUS.CANCELLED:
      return 'Cancelled';
    default:
      return status;
  }
}

/**
 * Get color class for a status (for UI styling)
 */
export function getStatusColor(status: TaskStatus): string {
  switch (status) {
    case TASK_STATUS.TODO:
    case TASK_STATUS.OPEN:
      return 'text-gray-500';
    case TASK_STATUS.IN_PROGRESS:
      return 'text-blue-500';
    case TASK_STATUS.BLOCKED:
      return 'text-red-500';
    case TASK_STATUS.COMPLETED:
      return 'text-green-500';
    case TASK_STATUS.CANCELLED:
      return 'text-gray-400';
    default:
      return 'text-gray-500';
  }
}

// Export all statuses as an array for dropdowns/selects
export const ALL_STATUSES: TaskStatus[] = Object.values(TASK_STATUS);

// Status options for UI components
export const STATUS_OPTIONS = ALL_STATUSES.map((status) => ({
  value: status,
  label: getStatusLabel(status),
  color: getStatusColor(status),
}));
